import { ComponentProps, useState, useEffect, useMemo } from 'react';

import Typography from '~/shared/components/Typography';
import { Volume } from '~/shared/types/api';
import Tag from '~/shared/components/Tag';
import { DatafySwitchVolume } from '~/shared/context/DatafySwitchProvider';
import Tooltip from '~/shared/components/Tooltip';
import { formatBytes, formatBytesWithValueAndUnit } from '~/shared/utils/formatNumber';
import { resolveVolumeCurrentSizeBytes } from '~/shared/utils/exportUtils';
import SizeCell from '~/shared/components/SizeCell';
import { usePermission } from '~/shared/hooks/usePermission';

import AutoScaleRenderer from '../FleetManagerPage/AllVolumesPage/AllVolumesTable/AutoScaleRenderer';
// import VolumeActionsMenu from '../common/VolumeActionsMenu';

export default function Details({ volume }: { volume: Volume }) {
  const canWrite = usePermission('datafy.write.autoscaling');
  const getWindowWidth = (width: number) => {
    if (width >= 2560) {
      return 2560;
    }
    if (width >= 1920) {
      return 1920;
    }
    if (width >= 1440) {
      return 1440;
    }
    return 1280;
  };

  const [maxVisible, setMaxVisible] = useState(() =>
    typeof window !== 'undefined' ? getWindowWidth(window.innerWidth) : 2
  );

  useEffect(() => {
    const handleResize = () => {
      setMaxVisible(getWindowWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="h-fit font-family-outfit">
      <div className="mt-3.5 grid grid-cols-2">
        {getInfoItems(volume, [], maxVisible, canWrite).map((item, index, array) => (
          <InfoCard key={item.title} {...item} isLastTwo={index >= array.length - 2} />
        ))}
      </div>
    </div>
  );
}

function InfoCard({
  title,
  value,
  spanTwo = false,
  sm = false,
  volume,
  isLastTwo = false,
}: {
  title: string;
  value: string | React.ReactNode | ((volume: Volume) => React.ReactNode);
  spanTwo?: boolean;
  sm?: boolean;
  volume?: Volume;
  isLastTwo?: boolean;
}) {
  // Handle function values - ensure we always get a ReactNode
  const renderedValue = useMemo(() => {
    if (typeof value === 'function' && volume) {
      return value(volume);
    }
    return value;
  }, [value, volume]);

  return (
    <div
      className={`flex border-b border-gray-200 items-center ${spanTwo ? 'col-span-2' : ''} ${isLastTwo ? 'border-b-0' : ''}`}
      style={{
        height: sm
          ? '48px'
          : 'clamp(50px, calc(50px + (60 - 50) * ((100vw - 1024px) / (1440 - 1024))), 60px)',
      }}
    >
      <Typography variant="p3" color="text-gray-700" className="px-3 min-w-[110px]">
        {title}
      </Typography>
      {typeof renderedValue === 'string' || typeof renderedValue === 'number' ? (
        <>
          <Typography
            variant="p3"
            className="px-3 font-family-inter"
            fontWeight={500}
            truncate
            // title={typeof renderedValue === 'string' ? renderedValue : undefined}
            data-tooltip-id={typeof renderedValue === 'string' ? `key-${renderedValue}` : undefined}
          >
            {renderedValue}
          </Typography>
          <Tooltip id={`key-${renderedValue}`} content={renderedValue.toString()} />
        </>
      ) : (
        <div className="flex flex-1 min-w-0 px-3 my-auto">{renderedValue as React.ReactNode}</div>
      )}
    </div>
  );
}

function getInfoItems(
  volume: Volume,
  _datafySwitchVolumes: DatafySwitchVolume[],
  maxVisible: number,
  canWrite: boolean
): (ComponentProps<typeof InfoCard> & { spanTwo?: boolean; sm?: boolean })[] {
  const currentSize = resolveVolumeCurrentSizeBytes(volume);
  const originalSize = volume.diskSize;
  const { value: currentSizeValue, unit: currentSizeUnit } = formatBytesWithValueAndUnit(
    currentSize ?? 0,
    1
  );
  const { value: originalSizeValue, unit: originalSizeUnit } = formatBytesWithValueAndUnit(
    originalSize ?? 0,
    1
  );

  return [
    {
      title: 'AutoScaling',
      value: (
        <div className="flex w-full flex-1 min-w-0 items-center gap-3">
          <AutoScaleRenderer
            volume={volume}
            setSelectedVolumes={() => { }}
            canWrite={canWrite}
          />
          {/* <div className="ml-auto shrink-0">
            <VolumeActionsMenu volume={volume} />
          </div> */}
        </div>
      ),
      spanTwo: true,
      sm: true,
    },
    { title: 'Volume ID', value: volume.volumeId || 'N/A' },
    {
      title: 'Instance ID',
      value:
        volume.instanceId && volume.instanceId !== 'N/A' ? (
          <>
            <Tag
              size="sm"
              text={volume.instanceId}
              variant="instance"
              showFullText={maxVisible >= 1440}
              dataTooltipId={`instance-${volume.volumeId}`}
            />
            <Tooltip id={`instance-${volume.volumeId}`} content={volume.instanceId} />
          </>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Utilization',
      value:
        volume.utilization && volume.utilization > 0 ? `${Math.round(volume.utilization)}%` : 'N/A',
    },
    {
      title: 'Used',
      value:
        volume.diskUsed && volume.diskUsed > 0 ? (
          <>
            <SizeCell size={volume.diskUsed} dataTooltipId={`used-${volume.volumeId}`} />
            <Tooltip
              id={`used-${volume.volumeId}`}
              content={`${formatBytes(volume.diskUsed)} used`}
            />
          </>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Current Size',
      value:
        currentSize !== undefined && currentSize !== null ? (
          <>
            <SizeCell size={currentSize} dataTooltipId={`size-${volume.volumeId}`} />
            <Tooltip
              id={`size-${volume.volumeId}`}
              content={`${currentSizeValue} ${currentSizeUnit}`}
            />
          </>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Original Size',
      value: volume.diskSize ? (
        <>
          <SizeCell size={volume.diskSize} dataTooltipId={`original-size-${volume.volumeId}`} />
          <Tooltip
            id={`original-size-${volume.volumeId}`}
            content={`${originalSizeValue} ${originalSizeUnit}`}
          />
        </>
      ) : (
        'N/A'
      ),
    },
    { title: 'IOPS', value: volume.iops ? `${volume.iops}` : 'N/A' },
    { title: 'Throughput', value: volume.throughput ? `${volume.throughput}` : 'N/A' },
    {
      title: 'Cluster',
      value:
        volume.clusterName && volume.clusterName !== 'N/A' ? (
          <>
            <Tag
              size="sm"
              text={volume.clusterName}
              variant="cluster"
              showFullText={maxVisible >= 3}
              dataTooltipId={`cluster-${volume.volumeId}`}
            />
            <Tooltip id={`cluster-${volume.volumeId}`} content={volume.clusterName} />
          </>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Node Group',
      value:
        volume.nodeGroupName && volume.nodeGroupName !== 'N/A' ? (
          <div className="flex items-center gap-1 my-auto">
            <Tag
              size="sm"
              text={volume.nodeGroupName}
              variant="node"
              showFullText={maxVisible >= 3}
              dataTooltipId={`nodegroup-${volume.volumeId}`}
            />
            <Tooltip id={`nodegroup-${volume.volumeId}`} content={volume.nodeGroupName} />
          </div>
        ) : (
          'N/A'
        ),
    },
  ];
}
